import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { copyTextToClipboard } from '../utils/browserCompat'; // http 站点没有 navigator.clipboard

/**
 * Error Boundary 组件
 * 捕获子组件树中的 JavaScript 错误，防止整个应用白屏
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    // 更新 state 使下一次渲染能够显示降级后的 UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // 记录错误信息
    console.error('🔴 ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    this.setState({ errorInfo });

    try {
      const payload = {
        message: error?.toString?.() || String(error || ''),
        stack: errorInfo?.componentStack || '',
        url: window.location.href,
        time: new Date().toISOString()
      };
      localStorage.setItem('cineart_last_error', JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleDismiss = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  handleToggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  handleCopyError = async () => {
    const payload = [
      this.state.error?.toString?.() || String(this.state.error || ''),
      this.state.errorInfo?.componentStack || ''
    ].join('\n');
    await copyTextToClipboard(payload); // 失败返回 false，报错页不再抛第二个错
  };

  handleClearCacheAndReload = async () => {
    try { localStorage.clear(); } catch {}
    try { sessionStorage.clear(); } catch {}
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      }
    } catch {}
    try {
      if (navigator.serviceWorker?.getRegistrations) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(reg => reg.unregister()));
      }
    } catch {}
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const urlParams = new URLSearchParams(window.location.search);
      const debugMode = urlParams.has('debug') || localStorage.getItem('cineart_debug_error') === '1';
      const showDetails = debugMode || this.state.showDetails;
      // 自定义的降级 UI
      return (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            background: '#1e293b',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '16px',
            padding: '32px 40px',
            maxWidth: '500px',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
            <h2 style={{ color: '#fff', fontSize: '20px', marginBottom: '12px' }}>
              哎呀，出了点问题
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
              图板遇到了意外错误。你可以尝试刷新页面，或者忽略错误继续操作。
            </p>
            
            {/* 错误详情 */}
            {showDetails && this.state.error && (
              <details style={{ 
                textAlign: 'left', 
                marginBottom: '24px', 
                background: 'rgba(0,0,0,0.3)', 
                padding: '12px', 
                borderRadius: '8px',
                maxHeight: '150px',
                overflow: 'auto'
              }}>
                <summary style={{ color: '#f87171', cursor: 'pointer', marginBottom: '8px' }}>
                  查看错误详情
                </summary>
                <pre style={{ 
                  color: '#fca5a5', 
                  fontSize: '11px', 
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  margin: 0
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={this.handleDismiss}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.target.style.borderColor = 'rgba(255,255,255,0.4)'}
                onMouseLeave={e => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
              >
                忽略继续
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.target.style.background = '#2563eb'}
                onMouseLeave={e => e.target.style.background = '#3b82f6'}
              >
                <RefreshCw size={14} />
                刷新页面
              </button>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: 16 }}>
              <button
                onClick={this.handleToggleDetails}
                style={{
                  padding: '6px 10px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                {showDetails ? '隐藏错误详情' : '查看错误详情'}
              </button>
              <button
                onClick={this.handleCopyError}
                style={{
                  padding: '6px 10px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                复制错误
              </button>
              <button
                onClick={this.handleClearCacheAndReload}
                style={{
                  padding: '6px 10px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                清理缓存并重载
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
